import SimpleDialog from '../SimpleDialog/SimpleDialog';
import React, { useEffect, useState } from 'react';
import { ValidatedField } from '../../../store/api.d';
import { Role } from '../../../store/conference/types';
import { AppState } from '../../../store/state';
import { selectErrorState, selectInfo, selectTrackRoles } from '../../../store/selectors';
import { AppDispatch } from '../../../store/store';
import { connect } from 'react-redux';
import infoSlice from '../../../store/info/slice';
import AddPersonForm from '../../AddPersonForm/AddPersonForm';
import { getUserByEmail } from '../../../store/user/sagas';
import { AddPersonDTO } from '../../../store/info/types';
import { validationErrorTransformer } from '../../../store/api';
import { useTranslation } from 'react-i18next';
import errorSlice from '../../../store/error/slice';

interface Props {
  handleClose: () => void;
  loading: boolean;
  roleById: { [key: string]: Role };
  addPersonAction: (data: AddPersonDTO) => void;
  valErrors?: ValidatedField[]; // Validation errors in Redux
  clearValidationErrorsAction: () => void;
}

const AddPersonDialog: React.FC<Props> = ({
  handleClose,
  loading,
  roleById,
  addPersonAction,
  valErrors,
  clearValidationErrorsAction,
}) => {
  const { t, i18n } = useTranslation();
  const [FData, setFData] = useState<AddPersonDTO>({
    first_name: '',
    last_name: '',
    email: '',
    role: 0,
  });
  const [showExtraFields, setShowExtraFields] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<ValidatedField[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(loading);

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  const handleOk = async () => {
    setValidationErrors([]);
    clearValidationErrorsAction();
    setIsLoading(true);
    const res = await getUserByEmail(FData.email);
    switch (res.type) {
      case 'ok':
        const { data } = res.value;
        if (data.length > 0 || showExtraFields) {
          const foundUser = data[0];
          // Case user found
          addPersonAction({ ...FData, ...foundUser });
        } else {
          // Case user not found. Ask for extra fields
          setShowExtraFields(true);
          setIsLoading(false);
        }
        break;
      case 'validation-error':
        setValidationErrors(res.value.validation);
        setIsLoading(false);
        break;
      case 'error':
        break;
    }
  };

  let errors;
  if (validationErrors.length) {
    errors = validationErrorTransformer(validationErrors);
  } else if (valErrors && valErrors.length) {
    errors = validationErrorTransformer(valErrors);
  } else {
    errors = {};
  }

  return (
    <SimpleDialog
      title={t('Add person')}
      open={true}
      handleClose={handleClose}
      handleOk={handleOk}
      closeOnOk={false}
      disableOkButton={
        !FData.email || !FData.role || (showExtraFields && (!FData.first_name || !FData.last_name)) || isLoading
      }
    >
      <AddPersonForm
        FData={FData}
        onChange={(value) => setFData(value)}
        showExtraFields={showExtraFields}
        roleById={roleById}
        errors={errors}
      />
    </SimpleDialog>
  );
};

const mapStateToProps = (state: AppState) => ({
  loading: selectInfo(state).loadingAction,
  roleById: selectTrackRoles(state),
  valErrors: selectErrorState(state).validationErrors,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  addPersonAction: (data: AddPersonDTO) => dispatch(infoSlice.actions['USER:ADD'](data)),
  clearValidationErrorsAction: () => dispatch(errorSlice.actions['CLEAR:VALIDATION_ERRORS']()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddPersonDialog);
